import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  getBacktestHistory,
  getBrokers,
  runBacktest as runBacktestService,
} from "../services/api.broker";
import {
  runCustomBacktest,
  updateCatalogStrategy,
  updateCustomStrategy,
  updateStrategy,
} from "../services/api.strategies";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  DollarSign,
  Play,
  RotateCcw,
  Save,
  Target,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import { useAuth } from "../context/AuthContext";
import { getApiErrorMessage } from "../lib/apiErrors";
import { formatDateTimeDisplay, resolveDisplayTimezone } from "../lib/datetime";
import { instrumentOptionsFromBrokerCatalog } from "../lib/instruments";
import {
  buildStrategyOptions,
  buildRuntimeDefinition,
  getDefaultRuntimeParams,
  normalizeRuntimeDefinition,
} from "../lib/strategyRuntime";
import BacktestTradeChart from "./BacktestTradeChart";
import StrategyRuntimeFields from "./StrategyRuntimeFields";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "./ui/resizable";
import { ScrollArea } from "./ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
const TIMEFRAMES = [
  { value: "1", label: "1 minute" },
  { value: "5", label: "5 minutes" },
  { value: "15", label: "15 minutes" },
  { value: "60", label: "1 hour" },
];

const toDateInputValue = (date) => {
  const adjusted = new Date(date);
  adjusted.setMinutes(adjusted.getMinutes() - adjusted.getTimezoneOffset());
  return adjusted.toISOString().slice(0, 10);
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 90);
  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
  };
};

const formatMoney = (value, { showSign = false, absolute = false, fallback = "—" } = {}) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  const displayValue = absolute ? Math.abs(numeric) : numeric;
  const sign = showSign && numeric > 0 ? "+" : "";
  return `${sign}$${displayValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value, { showSign = false, fallback = "—" } = {}) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  const sign = showSign && numeric > 0 ? "+" : "";
  return `${sign}${numeric.toFixed(2)}%`;
};

const formatDecimal = (value, { digits = 2, fallback = "—", infinityFallback = "∞" } = {}) => {
  const numeric = Number(value);
  if (value == null) {
    return fallback;
  }
  if (!Number.isFinite(numeric)) {
    return infinityFallback;
  }
  return numeric.toFixed(digits);
};

const valueToneClass = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "text-white";
  }
  if (numeric > 0) {
    return "text-profit";
  }
  if (numeric < 0) {
    return "text-loss";
  }
  return "text-white";
};

const formatDataQualityLabel = (dataQuality) => {
  if (!dataQuality) return "Unknown quality";
  if (dataQuality.freshness === "stale") {
    return dataQuality.is_live ? "Live stale" : "Historical stale";
  }
  if (dataQuality.is_live) {
    return dataQuality.prefer_live ? "Broker-aligned live" : "Live";
  }
  return "Historical";
};

const dataQualityBadgeClassName = (dataQuality) => {
  if (!dataQuality) {
    return "border-zinc-700 bg-zinc-900 text-zinc-300";
  }
  if (dataQuality.freshness === "stale") {
    return "border-amber-500/30 bg-amber-500/10 text-amber-200";
  }
  if (dataQuality.is_live) {
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
  }
  return "border-cyan-500/30 bg-cyan-500/10 text-cyan-200";
};

const shortenParitySignature = (value) => {
  if (!value) return "—";
  const text = String(value).trim();
  return text ? text.slice(0, 8) : "—";
};

const formatTimestamp = (value, timezone) => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return formatDateTimeDisplay(date, timezone, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }) || "—";
};

const toFiniteNumberOr = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeConditionRules = (block) => {
  if (!block) return [];
  if (Array.isArray(block)) return block;
  if (Array.isArray(block.rules)) return block.rules;
  return [];
};

const normalizeConditionLogic = (block, fallback = "AND") => {
  if (!block || Array.isArray(block)) return fallback;
  const value = String(block.logic || "").toUpperCase();
  return value === "OR" ? "OR" : "AND";
};

export default function BacktestPanel({
  visualStrategies = [],
  catalogStrategies = [],
  customStrategies = [],
  onStrategiesUpdated,
}) {
  const { user } = useAuth();
  const appTimeZone = resolveDisplayTimezone(user?.timezone);
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [symbol, setSymbol] = useState("");
  const [strategy, setStrategy] = useState("TREND_FOLLOWING");
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [intervalMinutes, setIntervalMinutes] = useState("5");
  const [initialCapital, setInitialCapital] = useState(10000);
  const [positionSize, setPositionSize] = useState(1);
  const [stopLossTicks, setStopLossTicks] = useState(10);
  const [takeProfitTicks, setTakeProfitTicks] = useState(20);
  const [commissionPerContract, setCommissionPerContract] = useState(2.5);
  const [slippageTicks, setSlippageTicks] = useState(1);
  const [strategyRuntime, setStrategyRuntime] = useState(
    normalizeRuntimeDefinition(null, "TREND_FOLLOWING", "runtime", "backtest"),
  );
  const [results, setResults] = useState(null);
  const [selectedTradeNumber, setSelectedTradeNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingStrategyParams, setSavingStrategyParams] = useState(false);
  const [history, setHistory] = useState([]);
  const skipRuntimeSyncRef = useRef(false);

  const strategyOptions = useMemo(() => {
    return buildStrategyOptions({
      visualStrategies,
      catalogStrategies,
      customStrategies,
      includeBuiltIns: true,
      includeNone: false,
    });
  }, [visualStrategies, catalogStrategies, customStrategies]);

  const selectedVisualStrategy = useMemo(() => {
    if (!strategy.startsWith("visual:")) {
      return null;
    }
    const visualId = strategy.replace("visual:", "");
    return visualStrategies.find((item) => item.id === visualId) || null;
  }, [strategy, visualStrategies]);

  const selectedStrategyOption = useMemo(
    () => strategyOptions.find((item) => item.value === strategy) || null,
    [strategy, strategyOptions],
  );

  const strategyPersistence = useMemo(() => {
    const normalizedStrategy = String(strategy || "").trim();
    if (normalizedStrategy.startsWith("visual:")) {
      return {
        kind: "visual",
        id: normalizedStrategy.replace("visual:", ""),
        label: "saved visual strategy",
        canSave: true,
        helperText: "Saving here updates the strategy record used by future backtests, automation runs, and new trades linked to it.",
      };
    }
    if (normalizedStrategy.startsWith("custom:")) {
      return {
        kind: "custom",
        id: normalizedStrategy.replace("custom:", ""),
        label: "custom strategy",
        canSave: true,
        helperText: "Saving here updates the custom strategy used by future backtests, automation runs, and new trades linked to it.",
      };
    }
    if (normalizedStrategy.startsWith("catalog:")) {
      const isAdmin = user?.role === "admin";
      return {
        kind: "catalog",
        id: normalizedStrategy.replace("catalog:", ""),
        label: "catalog strategy",
        canSave: isAdmin,
        helperText: isAdmin
          ? "Saving here updates the catalog strategy so future trades using it pick up these parameters."
          : "Catalog strategies can only be updated by an admin.",
      };
    }
    return {
      kind: "runtime",
      id: null,
      label: "built-in runtime preset",
      canSave: false,
      helperText: "Built-in presets are local to this backtest. Save or clone a strategy first if you want parameters to persist.",
    };
  }, [strategy, user?.role]);

  useEffect(() => {
    if (skipRuntimeSyncRef.current) {
      skipRuntimeSyncRef.current = false;
      return;
    }
    setStrategyRuntime(
      normalizeRuntimeDefinition(
        selectedStrategyOption?.runtime_definition || null,
        selectedStrategyOption?.runtime_definition?.engine || strategy,
        selectedStrategyOption?.runtime_definition?.language || "runtime",
        selectedStrategyOption?.runtime_definition?.source || "backtest",
      ),
    );
  }, [selectedStrategyOption, strategy]);

  const updateStrategyRuntimeParam = (key, value) => {
    setStrategyRuntime((current) => ({
      ...current,
      params: {
        ...current.params,
        [key]: value,
      },
    }));
  };

  const handleRestoreStrategyDefaults = () => {
    const sourceRuntime = selectedStrategyOption?.runtime_definition || strategyRuntime;
    const engine = sourceRuntime?.engine || strategyRuntime.engine || "TREND_FOLLOWING";
    const language = sourceRuntime?.language || strategyRuntime.language || "runtime";
    const source = sourceRuntime?.source || strategyRuntime.source || "backtest";
    setStrategyRuntime(buildRuntimeDefinition(engine, getDefaultRuntimeParams(engine), language, source));
    toast.success("Strategy inputs restored to engine defaults. Save to persist the reset.");
  };

  const handleSaveStrategyRuntime = async () => {
    if (!strategyPersistence.canSave || !strategyPersistence.id) {
      toast.warning(strategyPersistence.helperText);
      return;
    }

    const normalizedRuntime = buildRuntimeDefinition(
      strategyRuntime.engine || selectedStrategyOption?.runtime_definition?.engine || "TREND_FOLLOWING",
      strategyRuntime.params,
      strategyRuntime.language || selectedStrategyOption?.runtime_definition?.language || "runtime",
      selectedStrategyOption?.runtime_definition?.source || strategyRuntime.source || "runtime",
    );

    setSavingStrategyParams(true);
    try {
      if (strategyPersistence.kind === "visual") {
        await updateStrategy(strategyPersistence.id, { runtime: normalizedRuntime });
      } else if (strategyPersistence.kind === "custom") {
        await updateCustomStrategy(strategyPersistence.id, { runtime: normalizedRuntime });
      } else if (strategyPersistence.kind === "catalog") {
        await updateCatalogStrategy(strategyPersistence.id, { runtime: normalizedRuntime });
      } else {
        toast.warning("Built-in presets cannot be saved directly.");
        return;
      }

      toast.success(`Updated ${strategyPersistence.label} parameters. Future trades using this strategy will pick up the saved values.`);
      if (typeof onStrategiesUpdated === "function") {
        await onStrategiesUpdated();
      }
    } catch (error) {
      toast.error(`Failed to save strategy parameters: ${getApiErrorMessage(error, "Request failed")}`);
    } finally {
      setSavingStrategyParams(false);
    }
  };

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await getBacktestHistory({ limit: 5 });
        setHistory(response.data.backtests || []);
      } catch (error) {
        console.error("Error fetching backtest history:", error);
      }
    };

    fetchHistory();
  }, []);

  useEffect(() => {
    const fetchBrokerInstrumentCatalog = async () => {
      try {
        const response = await getBrokers();
        const options = instrumentOptionsFromBrokerCatalog(
          Array.isArray(response.data) ? response.data : [],
        );
        setSymbolOptions(options);
        setSymbol((current) => (
          options.some((option) => option.value === current)
            ? current
            : (options[0]?.value || "")
        ));
      } catch (error) {
        console.error("Error fetching backtest instrument catalog:", error);
        setSymbolOptions([]);
      }
    };

    fetchBrokerInstrumentCatalog();
  }, []);

  useEffect(() => {
    if (!results?.trades?.length) {
      setSelectedTradeNumber(null);
      return;
    }
    setSelectedTradeNumber(results.trades[results.trades.length - 1].trade_number || null);
  }, [results]);

  const runBacktest = async () => {
    if (!symbol) {
      toast.error("No broker instruments are configured. Update the broker instrument list in Admin first.");
      return;
    }

    if (new Date(`${startDate}T00:00:00`) > new Date(`${endDate}T23:59:59`)) {
      toast.error("Start date must be before end date");
      return;
    }

    setLoading(true);
    try {
      const isCatalog = strategy.startsWith("catalog:");
      const isCustom = strategy.startsWith("custom:");
      const isVisual = strategy.startsWith("visual:");
      const basePayload = {
        symbol,
        start_date: startDate,
        end_date: endDate,
        initial_capital: Number(initialCapital),
        position_size: Number(positionSize),
        stop_loss_ticks: Number(stopLossTicks),
        take_profit_ticks: Number(takeProfitTicks),
        interval_minutes: Number(intervalMinutes),
        commission_per_contract: Number(commissionPerContract),
        slippage_ticks: Number(slippageTicks),
      };

      if (isCustom) {
        const customId = strategy.replace("custom:", "");
        const response = await runCustomBacktest(customId, {
          ...basePayload,
          runtime: strategyRuntime,
        });
        setResults(response.data.results || response.data);
        toast.success("Custom strategy backtest completed");
        return;
      }

      const response = await runBacktestService({
        ...basePayload,
        strategy: strategyRuntime.engine || strategy,
        strategy_ref: strategy,
        strategy_id: isCatalog ? strategy.replace("catalog:", "") : null,
        runtime: strategyRuntime,
      });

      setResults(response.data);
      toast.success("Backtest completed successfully");

      try {
        const historyResponse = await getBacktestHistory({ limit: 5 });
        setHistory(historyResponse.data.backtests || []);
      } catch (historyError) {
        console.error("Error refreshing backtest history:", historyError);
      }
    } catch (error) {
      console.error("Error running backtest:", error);
      toast.error(`Failed to run backtest: ${getApiErrorMessage(error, "Request failed")}`);
    } finally {
      setLoading(false);
    }
  };

  const equityCurveData = results?.equity_curve?.map((value, index) => ({
    index,
    equity: value,
  })) || [];

  const fieldLabelClassName = "text-sm font-medium text-zinc-300";
  const fieldInputClassName = "border-zinc-700 bg-zinc-800 text-sm text-white";
  const selectContentClassName = "border-zinc-800 bg-zinc-900 text-white";
  const selectItemClassName = "text-sm text-white focus:bg-zinc-800 focus:text-white";
  const resultsStrategyLabel = results?.strategy_used?.name || strategyOptions.find((item) => item.value === strategy)?.label || "Backtest";
  const selectedTrade = results?.trades?.find((trade) => trade.trade_number === selectedTradeNumber) || null;
  const chartOverlays = results?.chart?.overlays || [];
  const backtestDataQuality = results?.data_quality || null;
  const paritySignatureShort = shortenParitySignature(results?.parity_signature);
  const resultSummaryCards = results ? [
    {
      label: "Total P&L",
      value: formatMoney(results.total_pnl, { showSign: true }),
      tone: valueToneClass(results.total_pnl),
      icon: DollarSign,
      subvalue: formatMoney(results.final_capital),
      sublabel: "Final capital",
    },
    {
      label: "Return",
      value: formatPercent(results.return_pct, { showSign: true }),
      tone: valueToneClass(results.return_pct),
      icon: Target,
      subvalue: formatPercent(results.win_rate),
      sublabel: "Win rate",
    },
    {
      label: "Profit Factor",
      value: formatDecimal(results.profit_factor, {
        digits: 2,
        fallback: results.losing_trades === 0 && results.winning_trades > 0 ? "∞" : "—",
      }),
      tone: "text-white",
      icon: BarChart3,
      subvalue: formatMoney(results.expectancy, { showSign: true }),
      sublabel: "Expectancy / trade",
    },
    {
      label: "Max Drawdown",
      value: formatPercent(results.max_drawdown),
      tone: "text-loss",
      icon: AlertTriangle,
      subvalue: formatMoney(results.max_drawdown_amount),
      sublabel: "Drawdown amount",
    },
  ] : [];
  const detailStats = results ? [
    { label: "Total Trades", value: results.total_trades ?? 0, tone: "text-white" },
    { label: "Winners", value: results.winning_trades ?? 0, tone: "text-profit" },
    { label: "Losers", value: results.losing_trades ?? 0, tone: "text-loss" },
    { label: "Avg Win", value: formatMoney(results.avg_win), tone: "text-profit" },
    { label: "Avg Loss", value: formatMoney(results.avg_loss, { absolute: true }), tone: "text-loss" },
    { label: "Fees", value: formatMoney(results.commission_paid), tone: "text-white" },
    { label: "Slippage", value: formatMoney(results.slippage_cost), tone: "text-white" },
    { label: "Sharpe", value: formatDecimal(results.sharpe_ratio, { digits: 3 }), tone: "text-white" },
    { label: "Sortino", value: formatDecimal(results.sortino_ratio, { digits: 3 }), tone: "text-white" },
    { label: "Avg Hold", value: `${formatDecimal(results.avg_bars_held, { digits: 1, fallback: "0.0" })} bars`, tone: "text-white" },
    { label: "Long / Short", value: `${results.long_trades ?? 0} / ${results.short_trades ?? 0}`, tone: "text-white" },
    { label: "Exposure", value: formatPercent(results.exposure_pct), tone: "text-white" },
  ] : [];
  const assumptions = results ? [
    { label: "Range", value: `${results.requested_range?.start_date || startDate} to ${results.requested_range?.end_date || endDate}` },
    { label: "Timeframe", value: `${results.interval_minutes || intervalMinutes} min` },
    { label: "Data Source", value: (results.data_source || "simulated").toUpperCase() },
    { label: "Data Quality", value: formatDataQualityLabel(backtestDataQuality) },
    { label: "Parity Signature", value: paritySignatureShort },
    { label: "Symbol", value: results.instrument?.symbol || symbol },
    { label: "Tick Size", value: formatDecimal(results.instrument?.tick_size) },
    { label: "Tick Value", value: formatMoney(results.instrument?.tick_value) },
    { label: "Commission / Side", value: formatMoney(results.assumptions?.commission_per_contract) },
    { label: "Slippage", value: `${formatDecimal(results.assumptions?.slippage_ticks)} ticks` },
  ] : [];

  const loadHistoryEntry = (entry) => {
    const request = entry?.request || {};
    const requestStrategyRef = String(request.strategy_ref || request.strategy || "").trim();
    const fallbackStrategy = String(request.strategy || "TREND_FOLLOWING").trim() || "TREND_FOLLOWING";
    const nextStrategyValue = strategyOptions.some((item) => item.value === requestStrategyRef)
      ? requestStrategyRef
      : (strategyOptions.some((item) => item.value === fallbackStrategy) ? fallbackStrategy : strategy);
    const selectedOption = strategyOptions.find((item) => item.value === nextStrategyValue) || null;

    if (request.symbol) {
      setSymbol(String(request.symbol).toUpperCase());
    }
    if (request.start_date) {
      setStartDate(String(request.start_date));
    }
    if (request.end_date) {
      setEndDate(String(request.end_date));
    }
    if (request.interval_minutes != null) {
      setIntervalMinutes(String(request.interval_minutes));
    }
    if (request.initial_capital != null) {
      setInitialCapital(toFiniteNumberOr(request.initial_capital, initialCapital));
    }
    if (request.position_size != null) {
      setPositionSize(toFiniteNumberOr(request.position_size, positionSize));
    }
    if (request.stop_loss_ticks != null) {
      setStopLossTicks(toFiniteNumberOr(request.stop_loss_ticks, stopLossTicks));
    }
    if (request.take_profit_ticks != null) {
      setTakeProfitTicks(toFiniteNumberOr(request.take_profit_ticks, takeProfitTicks));
    }
    if (request.commission_per_contract != null) {
      setCommissionPerContract(toFiniteNumberOr(request.commission_per_contract, commissionPerContract));
    }
    if (request.slippage_ticks != null) {
      setSlippageTicks(toFiniteNumberOr(request.slippage_ticks, slippageTicks));
    }

    skipRuntimeSyncRef.current = true;
    setStrategy(nextStrategyValue);
    setStrategyRuntime(
      normalizeRuntimeDefinition(
        request.runtime && typeof request.runtime === "object" ? request.runtime : selectedOption?.runtime_definition || null,
        request.runtime?.engine || selectedOption?.runtime_definition?.engine || fallbackStrategy,
        request.runtime?.language || selectedOption?.runtime_definition?.language || "runtime",
        request.runtime?.source || selectedOption?.runtime_definition?.source || "backtest_history",
      ),
    );

    if (entry?.results && typeof entry.results === "object") {
      setResults(entry.results);
      toast.success("Backtest restored from recent tests");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Backtest</h2>
          <p className="mt-1 text-sm text-zinc-400">
            Test your strategies before linking them to an account in Automation.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 xl:items-stretch">
        <div className="col-span-12 xl:col-span-3 space-y-4 xl:flex xl:h-full xl:flex-col xl:space-y-0 xl:gap-4">
          <Card className="border-zinc-800 bg-zinc-900">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-white">Parameters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-4">
              <div className="space-y-2">
                <Label className={fieldLabelClassName}>Symbol</Label>
                <Select value={symbol || undefined} onValueChange={setSymbol} disabled={!symbolOptions.length}>
                  <SelectTrigger className={fieldInputClassName} data-testid="backtest-symbol-select">
                    <SelectValue placeholder="Select symbol" />
                  </SelectTrigger>
                  <SelectContent className={selectContentClassName}>
                    {symbolOptions.map((symbolOption) => (
                      <SelectItem key={symbolOption.value} value={symbolOption.value} className={selectItemClassName}>
                        {symbolOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className={fieldLabelClassName}>Strategy</Label>
                <Select value={strategy} onValueChange={setStrategy}>
                  <SelectTrigger className={fieldInputClassName} data-testid="backtest-strategy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={selectContentClassName}>
                    {strategyOptions.map((strategyOption) => (
                      <SelectItem key={strategyOption.value} value={strategyOption.value} className={selectItemClassName}>
                        {strategyOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className={fieldLabelClassName}>Strategy Inputs</Label>
                <StrategyRuntimeFields
                  engine={strategyRuntime.engine}
                  params={strategyRuntime.params}
                  onChange={updateStrategyRuntimeParam}
                  gridClassName="grid grid-cols-1 gap-3 sm:grid-cols-2"
                  labelClassName={fieldLabelClassName}
                  inputClassName={fieldInputClassName}
                  switchCardClassName="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3"
                  hintClassName="text-xs text-zinc-500"
                />
              </div>

              <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="max-w-xl">
                    <p className="text-sm font-medium text-white">Persist Strategy Parameters</p>
                    <p className="mt-1 text-xs leading-5 text-zinc-400">
                      {strategyPersistence.helperText}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRestoreStrategyDefaults}
                      className="border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Restore Defaults
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSaveStrategyRuntime}
                      disabled={!strategyPersistence.canSave || savingStrategyParams}
                      className="border-blue-500/30 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {savingStrategyParams ? <span className="loading-spinner mr-2" /> : <Save className="mr-2 h-4 w-4" />}
                      Save to Strategy
                    </Button>
                  </div>
                </div>
              </div>

              {selectedVisualStrategy && (
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                  <p className="text-sm font-medium text-white">Saved Visual Parameters</p>
                  <p className="mt-1 text-xs text-zinc-400">
                    These are the same template parameters stored when this strategy was created.
                  </p>
                  <div className="mt-3 space-y-2 text-xs text-zinc-400">
                    <div>
                      <span className="text-zinc-500">Indicators:</span>{" "}
                      {Array.isArray(selectedVisualStrategy.indicators) && selectedVisualStrategy.indicators.length
                        ? selectedVisualStrategy.indicators
                            .map((indicator) => (typeof indicator === "string" ? indicator : indicator?.type || "IND"))
                            .join(", ")
                        : "None"}
                    </div>
                    <div>
                      <span className="text-zinc-500">Entry rules:</span>{" "}
                      {normalizeConditionRules(selectedVisualStrategy.conditions?.entry).length} (
                      {normalizeConditionLogic(selectedVisualStrategy.conditions?.entry, "AND")})
                    </div>
                    <div>
                      <span className="text-zinc-500">Exit rules:</span>{" "}
                      {normalizeConditionRules(selectedVisualStrategy.conditions?.exit).length} (
                      {normalizeConditionLogic(selectedVisualStrategy.conditions?.exit, "OR")})
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className={fieldLabelClassName}>Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={fieldInputClassName}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={fieldLabelClassName}>End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={fieldInputClassName}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className={fieldLabelClassName}>Timeframe</Label>
                <Select value={intervalMinutes} onValueChange={setIntervalMinutes}>
                  <SelectTrigger className={fieldInputClassName}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={selectContentClassName}>
                    {TIMEFRAMES.map((timeframe) => (
                      <SelectItem key={timeframe.value} value={timeframe.value} className={selectItemClassName}>
                        {timeframe.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className={fieldLabelClassName}>Initial Capital ($)</Label>
                <Input
                  type="number"
                  value={initialCapital}
                  onChange={(event) => setInitialCapital(Number(event.target.value))}
                  className={fieldInputClassName}
                  min={1000}
                  step={1000}
                  data-testid="backtest-capital-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={fieldLabelClassName}>Position Size</Label>
                <Input
                  type="number"
                  value={positionSize}
                  onChange={(event) => setPositionSize(Number(event.target.value))}
                  className={fieldInputClassName}
                  min={1}
                  max={10}
                  data-testid="backtest-position-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={fieldLabelClassName}>Stop Loss (Ticks)</Label>
                <Input
                  type="number"
                  value={stopLossTicks}
                  onChange={(event) => setStopLossTicks(Number(event.target.value))}
                  className={fieldInputClassName}
                  min={1}
                  max={100}
                  data-testid="backtest-stoploss-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={fieldLabelClassName}>Take Profit (Ticks)</Label>
                <Input
                  type="number"
                  value={takeProfitTicks}
                  onChange={(event) => setTakeProfitTicks(Number(event.target.value))}
                  className={fieldInputClassName}
                  min={1}
                  max={100}
                  data-testid="backtest-takeprofit-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className="flex min-h-[3.5rem] items-end">
                    <Label className={fieldLabelClassName}>Commission ($/contract/side)</Label>
                  </div>
                  <Input
                    type="number"
                    value={commissionPerContract}
                    onChange={(event) => setCommissionPerContract(Number(event.target.value))}
                    className={fieldInputClassName}
                    min={0}
                    step={0.25}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex min-h-[3.5rem] items-end">
                    <Label className={fieldLabelClassName}>Slippage (ticks)</Label>
                  </div>
                  <Input
                    type="number"
                    value={slippageTicks}
                    onChange={(event) => setSlippageTicks(Number(event.target.value))}
                    className={fieldInputClassName}
                    min={0}
                    step={0.25}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                <p className="text-sm font-medium text-white">Execution Assumptions</p>
                <p className="mt-1 text-xs leading-5 text-zinc-400">
                  Backtests now apply symbol-specific tick values, per-side commissions, adverse slippage, and close any open trade at the end of the test window.
                </p>
              </div>

              <Button
                className="w-full btn-trade"
                onClick={runBacktest}
                disabled={loading}
                data-testid="run-backtest-btn"
              >
                {loading ? <span className="loading-spinner mr-2" /> : <Play className="mr-2 h-4 w-4" />}
                Run Backtest
              </Button>
            </CardContent>
          </Card>

          <Card className="border-zinc-800 bg-zinc-900 xl:flex xl:flex-1 xl:flex-col">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-white">Recent Tests</CardTitle>
            </CardHeader>
            <CardContent className="p-0 xl:flex-1">
              <ScrollArea className="h-[200px] xl:h-full">
                {history.length > 0 ? (
                  <div className="divide-y divide-border">
                    {history.map((item, index) => (
                      <button
                        key={item.history_key || item.ran_at || index}
                        type="button"
                        onClick={() => loadHistoryEntry(item)}
                        className="w-full p-3 text-left transition-colors hover:bg-secondary/30"
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-sm font-medium text-white">
                            {item.request?.symbol} - {item.request?.strategy}
                          </span>
                          <Badge
                            variant={(item.results?.total_pnl ?? 0) >= 0 ? "default" : "destructive"}
                            className="text-[9px]"
                          >
                            {formatMoney(item.results?.total_pnl, { showSign: true })}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {item.results?.total_trades ?? 0} trades • {formatPercent(item.results?.win_rate)} win rate
                        </div>
                        <div className="mt-1 text-[11px] text-zinc-500">
                          {formatDataQualityLabel(item.results?.data_quality)} • Parity {shortenParitySignature(item.results?.parity_signature)}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-xs text-muted-foreground">No backtest history</div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        <div className="col-span-12 xl:col-span-9 space-y-4 xl:flex xl:h-full xl:flex-col xl:space-y-0 xl:gap-4">
          {results ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-zinc-700 bg-zinc-900 text-zinc-300">
                  {resultsStrategyLabel}
                </Badge>
                <Badge variant="outline" className="border-zinc-700 bg-zinc-900 text-zinc-300">
                  {results.instrument?.symbol || symbol}
                </Badge>
                <Badge variant="outline" className="border-zinc-700 bg-zinc-900 text-zinc-300">
                  {(results.data_source || "simulated").toUpperCase()}
                </Badge>
                <Badge variant="outline" className="border-zinc-700 bg-zinc-900 text-zinc-300">
                  {results.interval_minutes || intervalMinutes} min
                </Badge>
                <Badge variant="outline" className={dataQualityBadgeClassName(backtestDataQuality)}>
                  {formatDataQualityLabel(backtestDataQuality)}
                </Badge>
                <Badge variant="outline" className="border-zinc-700 bg-zinc-900 text-zinc-300">
                  Parity {paritySignatureShort}
                </Badge>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                {resultSummaryCards.map((metric) => {
                  const Icon = metric.icon;
                  return (
                    <Card key={metric.label} className="border-zinc-800 bg-zinc-900">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{metric.label}</p>
                            <p className={`text-2xl font-semibold ${metric.tone}`}>{metric.value}</p>
                            <p className="mt-1 text-xs text-zinc-500">
                              {metric.sublabel}: <span className="text-zinc-300">{metric.subvalue}</span>
                            </p>
                          </div>
                          <Icon className="h-8 w-8 opacity-50 text-zinc-500" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
                {detailStats.map((metric) => (
                  <Card key={metric.label} className="border-zinc-800 bg-zinc-900">
                    <CardContent className="p-3 text-center">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{metric.label}</p>
                      <p className={`text-lg font-semibold ${metric.tone}`}>{metric.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="border-zinc-800 bg-zinc-900">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-white">Run Assumptions</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
                  {assumptions.map((item) => (
                    <div key={item.label} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{item.label}</p>
                      <p className="mt-2 text-sm font-medium text-zinc-200">{item.value}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <ResizablePanelGroup direction="vertical" className="min-h-[980px] xl:flex-1">
                <ResizablePanel defaultSize={24} minSize={18}>
                  <Card className="flex h-full flex-col border-zinc-800 bg-zinc-900">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-white">Equity Curve</CardTitle>
                    </CardHeader>
                    <CardContent className="h-full min-h-[240px] flex-1 p-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={equityCurveData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                          <XAxis
                            dataKey="index"
                            tick={{ fill: "#71717a", fontSize: 10 }}
                            axisLine={{ stroke: "#262626" }}
                          />
                          <YAxis
                            domain={["auto", "auto"]}
                            tick={{ fill: "#71717a", fontSize: 10 }}
                            axisLine={{ stroke: "#262626" }}
                            tickFormatter={(value) => formatMoney(value, { fallback: "$0.00" })}
                          />
                          <ReferenceLine y={initialCapital} stroke="#71717a" strokeDasharray="3 3" />
                          <Area
                            type="monotone"
                            dataKey="equity"
                            fill={(results.total_pnl ?? 0) >= 0 ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)"}
                            stroke={(results.total_pnl ?? 0) >= 0 ? "#10B981" : "#EF4444"}
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </ResizablePanel>

                <ResizableHandle withHandle />

                <ResizablePanel defaultSize={42} minSize={28}>
                  <Card className="flex h-full flex-col border-zinc-800 bg-zinc-900">
                    <CardHeader className="space-y-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <CardTitle className="text-base font-semibold text-white">Trade Review Chart</CardTitle>
                          <p className="mt-1 text-xs text-zinc-400">
                            Drag to pan, scroll to zoom, and click a trade below to recenter the chart on that setup.
                          </p>
                        </div>
                        {selectedTrade && (
                          <Badge variant="outline" className="border-zinc-700 bg-zinc-900 text-zinc-300">
                            Trade #{selectedTrade.trade_number} • {selectedTrade.side} • {formatTimestamp(selectedTrade.entry_time, appTimeZone)}
                          </Badge>
                        )}
                      </div>
                      {chartOverlays.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {chartOverlays.map((overlay) => (
                            <div
                              key={overlay.id}
                              className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-zinc-300"
                            >
                              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: overlay.color }} />
                              {overlay.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardHeader>
                    <CardContent className="min-h-0 flex-1 p-4 pt-0">
                      <BacktestTradeChart
                        candles={results.chart?.candles || []}
                        overlays={results.chart?.overlays || []}
                        tradeMarkers={results.chart?.trade_markers || []}
                        focusTime={selectedTrade?.entry_time || null}
                      />
                    </CardContent>
                  </Card>
                </ResizablePanel>

                <ResizableHandle withHandle />

                <ResizablePanel defaultSize={34} minSize={22}>
                  <Card className="flex h-full flex-col border-zinc-800 bg-zinc-900">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-white">Trade Log (Last 20)</CardTitle>
                    </CardHeader>
                    <CardContent className="min-h-0 flex-1 p-0">
                      <ScrollArea className="h-full">
                        <div className="min-w-[1100px]">
                          <Table className="trading-table">
                            <TableHeader>
                              <TableRow>
                                <TableHead>#</TableHead>
                                <TableHead>Side</TableHead>
                                <TableHead>Entry Time</TableHead>
                                <TableHead>Exit Time</TableHead>
                                <TableHead>Entry</TableHead>
                                <TableHead>Exit</TableHead>
                                <TableHead>Gross</TableHead>
                                <TableHead>Fees</TableHead>
                                <TableHead>Net P&amp;L</TableHead>
                                <TableHead>Bars</TableHead>
                                <TableHead>MFE / MAE</TableHead>
                                <TableHead>Exit Reason</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {results.trades?.map((trade, index) => {
                                const isSelected = trade.trade_number === selectedTradeNumber;
                                return (
                                  <TableRow
                                    key={trade.trade_number || index}
                                    className={isSelected ? "bg-zinc-800/60" : "cursor-pointer"}
                                    onClick={() => setSelectedTradeNumber(trade.trade_number)}
                                  >
                                    <TableCell>{trade.trade_number || index + 1}</TableCell>
                                    <TableCell>
                                      <Badge variant={trade.side === "BUY" ? "default" : "destructive"}>{trade.side}</Badge>
                                    </TableCell>
                                    <TableCell>{formatTimestamp(trade.entry_time, appTimeZone)}</TableCell>
                                    <TableCell>{formatTimestamp(trade.exit_time, appTimeZone)}</TableCell>
                                    <TableCell>{formatDecimal(trade.entry_price, { digits: 2 })}</TableCell>
                                    <TableCell>{formatDecimal(trade.exit_price, { digits: 2 })}</TableCell>
                                    <TableCell className={valueToneClass(trade.gross_pnl)}>
                                      {formatMoney(trade.gross_pnl, { showSign: true })}
                                    </TableCell>
                                    <TableCell>{formatMoney(trade.fees)}</TableCell>
                                    <TableCell className={valueToneClass(trade.pnl)}>
                                      {formatMoney(trade.pnl, { showSign: true })}
                                    </TableCell>
                                    <TableCell>{trade.bars_held ?? "—"}</TableCell>
                                    <TableCell>
                                      {formatDecimal(trade.mfe_ticks, { digits: 1 })} / {formatDecimal(trade.mae_ticks, { digits: 1 })}
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className="text-[9px]">
                                        {trade.exit_reason}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {(!results.trades || results.trades.length === 0) && (
                                <TableRow>
                                  <TableCell colSpan={12} className="py-6 text-center text-sm text-zinc-500">
                                    No closed trades were produced for this run.
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </ResizablePanel>
              </ResizablePanelGroup>
            </>
          ) : (
            <Card className="border-zinc-800 bg-zinc-900">
              <CardContent className="p-16 text-center">
                <Activity className="mx-auto mb-4 h-16 w-16 text-muted-foreground opacity-30" />
                <h3 className="mb-2 text-lg font-bold uppercase tracking-wide">No Results Yet</h3>
                <p className="text-sm text-muted-foreground">
                  Configure your strategy parameters and click &quot;Run Backtest&quot; to see results
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
